import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";

/**
 * One savings goal's progress (`model.md` §4.3, `project.md` §5.3).
 *
 * Goal bars and budget bars look alike and mean opposite things, and the colour
 * rule is where that shows.
 *
 * **This bar is never red.** `project.md` §5.5 reserves red strictly for
 * overspend, and a savings goal has no overspend: passing the target is the
 * thing you were trying to do. Being behind on a target date is a fact, not an
 * alarm, so it is stated in words underneath rather than painted.
 *
 * **The fill is the clamped ratio and the words are the real one.** §4.3 clamps
 * "for display only", which is exactly this split: a bar cannot draw 150% of
 * itself, and the overshoot is worth saying out loud.
 */
export function GoalBar({
  savedMinor,
  targetMinor,
  currency,
  ratio,
  displayRatio,
  targetReached,
  isAchieved,
  label,
  monthlyPaceMinor,
}: {
  savedMinor: number;
  targetMinor: number;
  currency: string;
  ratio: number;
  displayRatio: number;
  targetReached: boolean;
  isAchieved: boolean;
  label: string;
  monthlyPaceMinor?: number | undefined;
}) {
  const remaining = targetMinor - savedMinor;
  const t = useT();
  const format = useFormat();
  const percent = Math.round(ratio * 100);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4 text-sm">
        <span className={`truncate ${isAchieved ? "text-muted" : ""}`}>
          {label}
          {isAchieved && ` · ${t("goal.achieved")}`}
        </span>
        <span className="num shrink-0 text-muted">
          {format.amount(savedMinor, currency)} / {format.amount(targetMinor, currency)}
        </span>
      </div>

      <div
        role="progressbar"
        aria-label={t("goal.barLabel", {
          label,
          saved: format.amount(savedMinor, currency),
          target: format.amount(targetMinor, currency),
        })}
        aria-valuemin={0}
        aria-valuemax={targetMinor}
        aria-valuenow={savedMinor}
        className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-track"
      >
        <div
          // One accent colour, no gradient (`project.md` §5.5). A goal that is
          // finished is muted rather than recoloured: it is done, not different.
          className={`h-full rounded-full ${isAchieved ? "bg-chart-out" : "bg-accent"}`}
          style={{ width: `${Math.max(0, Math.min(displayRatio, 1)) * 100}%` }}
        />
      </div>

      <p className="mt-1 text-xs text-muted" data-testid="goal-remaining">
        {targetReached
          ? t("goal.overTarget", { percent, amount: format.amount(-remaining, currency) })
          : t("goal.remaining", { amount: format.amount(remaining, currency) })}
        {monthlyPaceMinor !== undefined && !targetReached && (
          <span data-testid="goal-pace">
            {" · "}
            {t("goal.pace", { amount: format.amount(monthlyPaceMinor, currency) })}
          </span>
        )}
      </p>
    </div>
  );
}
